import React from "react";
import ReactDOM from 'react-dom/client';
import { ConfigProvider, App as AntdApp, notification, message } from 'antd';
import 'bootstrap/dist/css/bootstrap.min.css';
import "./assets/css/bootstrap.min.css";
import "./assets/css/style.css";
import "./assets/css/bela-theme.css";
import "./assets/css/select2.min.css";
import '../node_modules/bootstrap/dist/css/bootstrap.min.css';
import { QueryClientProvider, hydrate } from "react-query";
import { BrowserRouter } from "react-router-dom";
import { persistQueryClient } from "react-query/persistQueryClient-experimental";
import { createWebStoragePersistor } from "react-query/createWebStoragePersistor-experimental";
import { queryClient } from "./utils/queryClient";
import App from "./App";
import { SelectedUserProvider } from "./components/staff/SelectedUserProvider.jsx";

document.body.classList.add('bela-theme');

const belaNavOffsetTop = 100;

notification.config({
    top: belaNavOffsetTop,
    placement: 'topRight',
});

message.config({
    top: belaNavOffsetTop,
});

const belaAntTheme = {
    token: {
        colorPrimary: '#0d7377',
        colorSuccess: '#10b981',
        colorWarning: '#f59e0b',
        colorError: '#ef4444',
        colorInfo: '#14a3a8',
        borderRadius: 1,
        fontFamily: 'Inter, Poppins, sans-serif',
        zIndexPopupBase: 10100,
    },
    components: {
        Notification: {
            zIndexPopup: 10200,
        },
        Message: {
            zIndexPopup: 10200,
        },
        Pagination: {
            itemActiveBg: '#0d7377',
            itemActiveColorDisabled: '#fff',
            itemBg: '#ffffff',
            itemLinkBg: '#ffffff',
            colorPrimary: '#0d7377',
            colorPrimaryHover: '#095456',
            colorText: '#1e293b',
            colorBorder: '#e2e8f0',
        },
        Table: {
            headerBg: 'rgba(13, 115, 119, 0.06)',
            headerColor: '#095456',
        },
        Button: {
            primaryShadow: '0 2px 8px rgba(13, 115, 119, 0.25)',
        },
    },
};

// Dashboard aggregates: keep in memory for hours so returning to the dashboard
// is instant (no skeletons), and only refetch in the background when stale.
const DASHBOARD_QUERY_KEYS = new Set([
    'getDailyTotalVisitByBranch',
    'getDailyTotalNewPatientByBranch',
    'getDailyTotalInsuranceVisitByBranch',
    'getDailySalesByBranch',
    'getBranchTodayPaidInvoice',
    'getBranchAdminSales',
    'getRecentPatients',
    'getUpcomingVisitByBranch',
    'getPatientPerDept',
    'getUpcomingAppointments',
    'getVisitPerGender',
    'getTotals',
]);

DASHBOARD_QUERY_KEYS.forEach((key) => {
    queryClient.setQueryDefaults(key, {
        staleTime: 5 * 60 * 1000,
        cacheTime: 4 * 60 * 60 * 1000,
        refetchOnMount: true,
    });
});

// Lightweight dashboard aggregates are persisted to sessionStorage so a refresh
// restores them instantly instead of showing loading skeletons, then refetches
// silently in the background.
const shouldDehydrateQuery = (query) => {
    const key = query.queryKey;
    const first = Array.isArray(key) ? key[0] : key;
    return typeof first === 'string' && DASHBOARD_QUERY_KEYS.has(first);
};

const STORAGE_KEY = 'hmsv2-dashboard-cache';
const MAX_AGE = 1000 * 60 * 60 * 8;

try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
        const persisted = JSON.parse(raw);
        if (persisted && Date.now() - (persisted.timestamp || 0) < MAX_AGE) {
            hydrate(queryClient, persisted.clientState);
        }
    }
} catch (e) {
    // ignore corrupted/expired cache
}

const persistor = createWebStoragePersistor({
    storage: sessionStorage,
    key: STORAGE_KEY,
    throttleTime: 1000,
});

persistQueryClient({
    queryClient,
    persistor,
    maxAge: MAX_AGE,
    dehydrateOptions: { shouldDehydrateQuery },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
    <QueryClientProvider client={queryClient}>
        <ConfigProvider theme={belaAntTheme}>
            <AntdApp notification={{ top: belaNavOffsetTop, placement: 'topRight' }} message={{ top: belaNavOffsetTop }}>
                <BrowserRouter>
                    <SelectedUserProvider>
                        <App />
                    </SelectedUserProvider>
                </BrowserRouter>
            </AntdApp>
        </ConfigProvider>
    </QueryClientProvider>
);
